import {
  Checkbox,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material'
import { DialogCloseSaveButtons } from 'components/Common/DialogActionButtons/DialogCloseSaveButtons'
import { LoadingContainer } from 'components/Common/LoadingContainer'
import { QuantityField } from 'components/Common/UnitEditor/QuantityField'
import { LocalizedTypography } from 'components/Localization/LocalizedTypography'
import { useAppController } from 'customHooks/useAppController'
import { ProjectSupplierLogo } from 'features/BillOfMaterials/ProjectHeader/ProjectSupplierLogo'
import { projectSelectors } from 'features/BillOfMaterials/store/selectors/projectSelectors'
import { DeliveryTermsSelector } from 'features/DeliveryTerms/project/DeliveryTermsSelector'
import { PaymentTermsSelector } from 'features/PaymentTerms/project/PaymentTermsSelector'
import { isEqual } from 'lodash'
import {
  FormEvent,
  FormEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import {
  AddressDto,
  ContactPersonDto,
  ProjectDetailsDto,
  QuantityDto,
  UpdateProjectDetailsRequest,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { ShowException } from 'store/Application/appActions'
import { useAppSelector } from 'store/configureStore'
import { makeStyles } from 'tss-react/mui'
import { deepCompareIgnoringKeys } from 'utils/deepCompare'
import { ProjectDetailsController } from '../ProjectDetailsController'
import { ProjectAddressForm } from './ProjectAddressForm'

type ProjectContactInfoDialogProps = {
  open: boolean
  onClose: () => void
}

const useStyles = makeStyles({ name: 'ProjectDetailsDialog' })((theme) => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(0, 3),
    gap: theme.spacing(2),
    // marginBottom: theme.spacing(2),
    textTransform: 'lowercase',
  },
  contactPersonForm: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
    justifyContent: 'space-between',
  },
  title: {
    fontWeight: 600,
  },
  titleDiv: {
    marginBottom: theme.spacing(1),
  },
  contactPersonTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  // autoComplete: {
  //   width: '50%',
  //   minWidth: '250px',
  // },
  projectDetailsForm: {
    display: 'flex',
    gap: theme.spacing(2),
  },
}))

export const ProjectDetailsDialog = (props: ProjectContactInfoDialogProps) => {
  const { classes } = useStyles()
  const { t } = useTranslation('project')

  const hasBuyingPartyIdSelected = useAppSelector((state) => {
    return (
      state.project.activeProject?.buyingParty?.partyId !==
      '00000000-0000-0000-0000-000000000000'
    )
  })

  const contactInfo = useAppSelector(
    projectSelectors.projectBuyerContactInfoSelector,
    isEqual,
  )

  const isBuyerView = useAppSelector(
    projectSelectors.projectInBuyerViewSelector,
  )

  const projectReference = useAppSelector(
    projectSelectors.projectReferenceSelector,
  )
  const orderNumber = useAppSelector(
    projectSelectors.projectOrderNumberSelector,
  )

  const { controller, error } = useAppController(
    () => new ProjectDetailsController(),
  )

  const [projectDetails, setProjectDetails] = useState<
    ProjectDetailsDto | undefined
  >(undefined)

  const [deliveryAddress, setDeliveryAddress] = useState<null | AddressDto>(
    null,
  )
  const [postalAddress, setPostalAddress] = useState<null | AddressDto>(null)

  const [vatRatio, setVatRatio] = useState<QuantityDto>(null)

  const [postalAddressAsDeliveryAddress, setPostalAddressAsDeliveryAddress] =
    useState<boolean | null>(null)

  const buyerParty = useAppSelector(
    projectSelectors.projectBuyerSelector,
    isEqual,
  )

  const isEditable = useAppSelector(
    projectSelectors.ProjectEditableStateSelector,
  )

  useEffect(() => {
    controller
      .getProjectDetails()
      .then((projectDetails) => {
        setProjectDetails(projectDetails)
        setPostalAddress(projectDetails?.postalAddress)

        if (
          deepCompareIgnoringKeys(
            projectDetails?.deliveryAddress,
            projectDetails?.postalAddress,
          )
        ) {
          setPostalAddressAsDeliveryAddress(true)
        } else {
          setPostalAddressAsDeliveryAddress(false)
        }

        setDeliveryAddress(projectDetails?.deliveryAddress)

        setVatRatio(projectDetails.vatRatio)
      })
      .catch((err) => ShowException('Quotation Details', err))
  }, [controller])

  const GetContactPersonRequest = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      const formData = new FormData(e.currentTarget)

      const newContactPerson: ContactPersonDto = {
        firstName: formData.get('firstName') as string,
        lastName: formData.get('lastName') as string,
        email: formData.get('email') as string,
      }

      if (
        !deepCompareIgnoringKeys(
          newContactPerson,
          projectDetails?.contactPerson,
          ['id'],
        )
      ) {
        return newContactPerson
      }

      return null
    },
    [projectDetails?.contactPerson],
  )

  const GetAddressRequest = useCallback(() => {
    const req: { postalAddress?: AddressDto; deliveryAddress?: AddressDto } = {}

    if (postalAddressAsDeliveryAddress) {
      req.deliveryAddress = postalAddress
    }

    if (!isEqual(projectDetails.deliveryAddress, deliveryAddress)) {
      req.deliveryAddress = deliveryAddress
    }

    if (!isEqual(projectDetails.postalAddress, postalAddress)) {
      req.postalAddress = postalAddress
    }

    return req
  }, [
    deliveryAddress,
    postalAddress,
    postalAddressAsDeliveryAddress,
    projectDetails?.deliveryAddress,
    projectDetails?.postalAddress,
  ])

  const handleSubmit: FormEventHandler<HTMLFormElement> = async (e) => {
    try {
      e.preventDefault()

      const contactPerson = GetContactPersonRequest(e)
      const addresses = GetAddressRequest()

      const formData = new FormData(e.currentTarget)

      const req: UpdateProjectDetailsRequest = {
        projectReference: formData.get('projectReference') as string,
        orderNumber: formData.get('orderNumber') as string,
        deliveryTermsCode:
          (formData.get('delivery-terms') as string) === ''
            ? undefined
            : (formData.get('delivery-terms') as string),
        paymentTermsCode:
          (formData.get('payment-terms') as string) === ''
            ? undefined
            : (formData.get('payment-terms') as string),
        vatRatio: vatRatio,
        buyingPartyName: formData.get('buyingPartyName') as string,
        contactPerson: contactPerson,
        postalAddress: addresses.postalAddress,
        deliveryAddress: addresses.deliveryAddress,
      }

      await controller.updateProjectDetails(req)

      props.onClose()
    } catch (err) {
      ShowException('Unable to update', err)
    }
  }

  const isPostalAddressEditable = useMemo(() => {
    if (isBuyerView) {
      return false
    }

    if (hasBuyingPartyIdSelected) {
      return false
    }

    return true
  }, [hasBuyingPartyIdSelected, isBuyerView])

  const AddressFields = useMemo(() => {
    return (
      <div className={classes.content}>
        <ProjectAddressForm
          address={deliveryAddress || {}}
          setAddress={(address) => {
            setDeliveryAddress(address)
            if (postalAddressAsDeliveryAddress && isPostalAddressEditable) {
              setPostalAddress(address)
            } else {
              setPostalAddressAsDeliveryAddress(false)
            }
          }}
          titleText={t('common:delivery-address', 'delivery address')}
          classes={{ titleText: classes.title }}
          addressRequired
        />
        {isPostalAddressEditable && (
          <FormControlLabel
            control={
              <Checkbox
                checked={postalAddressAsDeliveryAddress}
                onChange={(e) => {
                  setPostalAddressAsDeliveryAddress(e.target.checked)
                  setPostalAddress(deliveryAddress)
                }}
                size="small"
              />
            }
            label={
              <LocalizedTypography
                translationKey="common:use-delivery-address-as-postal-address"
                variant="body2"
                color="primary"
              >
                use delivery address as postal address
              </LocalizedTypography>
            }
          />
        )}
        <Collapse
          in={isPostalAddressEditable && !postalAddressAsDeliveryAddress}
        >
          <div className={classes.content}>
            <ProjectAddressForm
              address={postalAddress || {}}
              setAddress={(address) => {
                setPostalAddress(address)
              }}
              titleText={t('common:postal-address', 'postal address')}
              classes={{ titleText: classes.title }}
              addressRequired
            />
          </div>
        </Collapse>

        {error['create-project']?.validation?.['address is required'] && (
          <Typography variant="body2" color="error" sx={{ fontWeight: 600 }}>
            {t('common:address-required', 'address is a required field')}
          </Typography>
        )}
      </div>
    )
  }, [
    classes.content,
    classes.title,
    deliveryAddress,
    t,
    isPostalAddressEditable,
    postalAddressAsDeliveryAddress,
    postalAddress,
    error,
  ])

  return (
    <Dialog
      open={props.open}
      onClose={(e, reason) => reason === 'escapeKeyDown' && props.onClose()}
      fullWidth
      maxWidth="lg"
      disableRestoreFocus
    >
      <div
        style={{
          display: 'flex',
          padding: '1rem 1.5rem',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <LocalizedTypography
          translationKey="project:project-details"
          variant="h6"
        >
          project details
        </LocalizedTypography>
        <ProjectSupplierLogo disableClick />
      </div>
      <LoadingContainer loading={projectDetails === undefined} noMessage>
        <form
          onSubmit={handleSubmit}
          id="contact-person-form"
          style={{ marginBottom: '1rem' }}
        >
          <DialogContent className={classes.dialogContent}>
            <div className={classes.content}>
              <div className={classes.projectDetailsForm}>
                <TextField
                  onFocus={(e) => e.target.select()}
                  name="orderNumber"
                  label={t('project:order-number')}
                  defaultValue={orderNumber}
                  slotProps={{
                    htmlInput: {
                      maxLength: 50,
                    },
                  }}
                  style={{
                    minWidth: '20ch',
                    maxWidth: '60ch',
                  }}
                  disabled={isBuyerView}
                />
                <TextField
                  onFocus={(e) => e.target.select()}
                  name="projectReference"
                  label={t('project:project-reference')}
                  defaultValue={projectReference}
                  required
                  fullWidth
                  slotProps={{
                    htmlInput: {
                      maxLength: 50,
                    },
                  }}
                  style={{
                    width: '100%',
                  }}
                />
                <QuantityField
                  value={vatRatio}
                  onChange={(quantity) => setVatRatio(quantity as QuantityDto)}
                  label={t('project:vat-ratio-no-unit', 'VAT')}
                  disabled={!isEditable || isBuyerView}
                  decimalScale={3}
                  fixedDecimalScale={false}
                />
              </div>
            </div>
            <div className={classes.content}>
              <LocalizedTypography
                translationKey="project:buying-party"
                variant="subtitle1"
                className={classes.title}
              >
                Buyer Company
              </LocalizedTypography>
              <TextField
                onFocus={(e) => e.target.select()}
                name="buyingPartyName"
                defaultValue={
                  projectDetails?.buyerCompanyName ?? buyerParty?.legalName
                }
                required
                disabled={isBuyerView}
                fullWidth
                slotProps={{
                  htmlInput: {
                    maxLength: 50,
                  },
                }}
              />
            </div>
            <div className={classes.content}>
              <div className={classes.contactPersonTitle}>
                <LocalizedTypography
                  translationKey="project:contact-person"
                  variant="subtitle1"
                  className={classes.title}
                >
                  Contact Person
                </LocalizedTypography>
              </div>
              <div className={classes.contactPersonForm}>
                <TextField
                  onFocus={(e) => e.target.select()}
                  name="firstName"
                  label={t('common:first-name')}
                  defaultValue={contactInfo?.firstName}
                  variant="standard"
                  fullWidth
                  slotProps={{
                    htmlInput: {
                      maxLength: 50,
                    },
                  }}
                />
                <TextField
                  onFocus={(e) => e.target.select()}
                  name="lastName"
                  label={t('common:last-name')}
                  defaultValue={contactInfo?.lastName}
                  variant="standard"
                  fullWidth
                  slotProps={{
                    htmlInput: {
                      maxLength: 50,
                    },
                  }}
                />
                <TextField
                  onFocus={(e) => e.target.select()}
                  name="email"
                  label={t('common:email')}
                  defaultValue={contactInfo?.email}
                  type="email"
                  variant="standard"
                  fullWidth
                  slotProps={{
                    htmlInput: {
                      maxLength: 80,
                    },
                  }}
                />
              </div>
            </div>
            {!isBuyerView && (
              <div
                style={{
                  display: 'flex',
                  marginTop: '8px',
                  gap: '1rem',
                }}
              >
                <PaymentTermsSelector
                  label={t('project:payment-terms')}
                  placeholder={t(
                    'common:payment-terms-select',
                    'select a payment term',
                  )}
                  name="payment-terms"
                  onPaymentTermSelected={(code) =>
                    setProjectDetails((prev) => ({
                      ...prev,
                      paymentTerms: {
                        ...prev.paymentTerms,
                        code: code,
                      },
                    }))
                  }
                  selectedPaymentTermCode={
                    projectDetails?.paymentTerms?.code || ''
                  }
                  onCleaned={() => {
                    setProjectDetails((prev) => ({
                      ...prev,
                      paymentTerms: null,
                    }))
                  }}
                />
                <DeliveryTermsSelector
                  label={t('project:delivery-terms')}
                  placeholder={t(
                    'common:delivery-terms-select',
                    'select a delivery term',
                  )}
                  name="delivery-terms"
                  onDeliveryTermSelected={(code) =>
                    setProjectDetails((prev) => ({
                      ...prev,
                      deliveryTerms: {
                        ...prev.deliveryTerms,
                        code: code,
                      },
                    }))
                  }
                  selectedDeliveryTermCode={
                    projectDetails?.deliveryTerms?.code || ''
                  }
                  onCleanDeliveryTerms={() => {
                    setProjectDetails((prev) => ({
                      ...prev,
                      deliveryTerms: null,
                    }))
                  }}
                />
              </div>
            )}
            {AddressFields}
            {/* <div className={classes.content}>
            <ProjectAddressForm
              address={deliveryAddress || postalAddress}
              setAddress={setDeliveryAddress}
              titleText={t('common:delivery-address', 'delivery address')}
              classes={{ titleText: classes.title, titleDiv: classes.titleDiv }}
              disabled={
                !isEditable ||
                (hasBuyingPartyIdSelected && postalAddressAsDeliveryAddress)
              }
              titleExtraComponent={
                hasBuyingPartyIdSelected && (
                  <FormControlLabel
                    sx={{
                      marginRight: '0',
                    }}
                    control={
                      <Checkbox
                        checked={postalAddressAsDeliveryAddress ?? false}
                        onChange={(e) => {
                          setPostalAddressAsDeliveryAddress(e.target.checked)
                          setDeliveryAddress(postalAddress)
                        }}
                        size="small"
                        disabled={!isEditable}
                      />
                    }
                    label={
                      <LocalizedTypography
                        translationKey="common:use-postal-address-as-delivery-address"
                        variant="body2"
                        color="primary"
                      >
                        Use postal address as delivery address
                      </LocalizedTypography>
                    }
                  />
                )
              }
            />
          </div> */}
          </DialogContent>
        </form>
      </LoadingContainer>
      <DialogActions>
        <DialogCloseSaveButtons
          onCloseButtonClicked={props.onClose}
          saveButtonDisabled={false}
          saveButtonFormId="contact-person-form"
        />
      </DialogActions>
    </Dialog>
  )
}
